import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useTranslation } from 'react-i18next';
import type { BugEvaluationAiDecline } from '../../../lib/bug-evaluation-api';

/** 一次判定が見送った課題を出すカードの props。 @graph-connects none */
export interface AiDeclinedCardProps {
  /** 見送られた判定（新しい順）。空なら何も描画しない */
  declines: BugEvaluationAiDecline[];
}

/**
 * 一次判定が見送った課題を理由つきで出すカード。
 *
 * **評価詳細ページへは送らない。** 見送りは評価本体を持たないので、詳細ページを開いても
 * 「評価データが見つかりません」になる。直すべきはチケット側なので Backlog へ送る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ評価一覧ページで、AI一次判定が見送った課題を見送り理由つきで一覧し、Backlogへ送るカード。
 * @graph-connects none
 */
export function AiDeclinedCard({ declines }: AiDeclinedCardProps) {
  const { t } = useTranslation();
  if (declines.length === 0) return null;

  return (
    <Card className="p-6 border-l-4 border-l-[var(--color-warning)]">
      <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
        {t('bugEvaluation.aiDeclinedTitle', '一次判定が見送ったバグ（{{total}} 件）', {
          total: declines.length,
        })}
      </h2>
      <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
        {t('bugEvaluation.aiDeclinedHint', '')}
      </p>
      <ul className="mt-4 space-y-4">
        {declines.map((decline) => (
          <li
            key={decline.issueKey}
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4"
          >
            <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
              <a
                href={`https://air-closet.backlog.jp/view/${decline.issueKey}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={decline.issueKey}
                className="text-sm font-semibold text-[var(--color-accent-primary)] hover:underline"
              >
                {decline.issueKey}
              </a>
              <span className="text-xs text-[var(--color-text-muted)]">
                {t('bugEvaluation.aiDeclinedAt', '見送った日時')}:{' '}
                {decline.submittedAt.slice(0, 16)}
              </span>
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">
              {decline.declineReason ??
                t('bugEvaluation.aiDeclinedReasonMissing', '理由が記録されていません')}
            </p>
          </li>
        ))}
      </ul>
    </Card>
  );
}
